import React, { useState } from 'react';
import { RefreshCw, Check, AlertTriangle, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function ListingSync() {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const syncListings = async () => {
    try {
      setSyncing(true);
      setError(null);
      setResult(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-listings-coordinator`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Sync failed');

      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync listings');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Real-time Listing Sync</h2>
          <p className="text-sm text-gray-600">
            Sync live listings from Zillow, ATTOM, and Estated
          </p>
        </div>
        <button
          onClick={syncListings}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
          <span>{syncing ? 'Syncing...' : 'Sync Now'}</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg mb-4">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2 text-green-600">
              <Check className="w-5 h-5" />
              <span>Sync completed</span>
            </div>
            <div className="text-sm">
              <span className="font-medium">{result.stats.total_listings}</span> active listings
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-3">Primary Data Sources</h3>
              <div className="space-y-2">
                {result.stats.primary_results.map((result: any, index: number) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{result.source.replace('sync-', '')}</span>
                    <div className="flex items-center gap-2">
                      {result.status === 'success' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="group relative">
                          <AlertTriangle className="w-4 h-4 text-red-500 cursor-help" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-white rounded shadow-lg text-xs w-48 hidden group-hover:block">
                            {result.error}
                          </div>
                        </div>
                      )}
                      <span>{result.count} listings</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-3">Data Enrichment</h3>
              <div className="space-y-2">
                {result.stats.enrichment_results.map((result: any, index: number) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{result.source.replace('sync-', '')}</span>
                    <div className="flex items-center gap-2">
                      {result.status === 'success' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="group relative">
                          <AlertTriangle className="w-4 h-4 text-red-500 cursor-help" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-white rounded shadow-lg text-xs w-48 hidden group-hover:block">
                            {result.error}
                          </div>
                        </div>
                      )}
                      <span>{result.count} enriched</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 p-4 bg-blue-50 text-blue-700 rounded-lg">
            <Info className="w-5 h-5" />
            <span>Success Rate: {result.stats.success_rate}</span>
          </div>
        </div>
      )}
    </div>
  );
}